import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Activity, Users } from 'lucide-react'

interface PageVisitors {
  path: string
  title: string
  visitors: number
}

interface RealtimeData {
  activeVisitors: number
  pages: PageVisitors[]
  timestamp: string
}

interface RealtimeWidgetProps {
  siteId: string
}

export function RealtimeWidget({ siteId }: RealtimeWidgetProps) {
  const [realtimeData, setRealtimeData] = useState<RealtimeData | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [useWebSocket, setUseWebSocket] = useState(true)

  useEffect(() => {
    let ws: WebSocket | null = null
    let reconnectTimeout: number | null = null
    let pollInterval: number | null = null

    const connectWebSocket = () => {
      try {
        // Try WebSocket connection first
        ws = new WebSocket(`wss://analytics.omattic.com/api/live?siteId=${siteId}`)

        ws.onopen = () => {
          console.log('[Realtime] WebSocket connected')
          setIsConnected(true)
          setUseWebSocket(true)
        }

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data)
            if (message.type === 'realtime_update' && message.data) {
              setRealtimeData(message.data)
            }
          } catch (error) {
            console.error('[Realtime] Error parsing message:', error)
          }
        }

        ws.onerror = (error) => {
          console.error('[Realtime] WebSocket error:', error)
          setIsConnected(false)
        }

        ws.onclose = () => {
          console.log('[Realtime] WebSocket closed, falling back to HTTP polling')
          setIsConnected(false)
          setUseWebSocket(false)

          // Try to reconnect after 10 seconds
          reconnectTimeout = setTimeout(connectWebSocket, 10000) as unknown as number
        }
      } catch (error) {
        console.error('[Realtime] Failed to create WebSocket:', error)
        setUseWebSocket(false)
      }
    }

    const fetchRealtime = async () => {
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch(
          `https://analytics.omattic.com/api/realtime?siteId=${siteId}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: 'include',
          }
        )
        if (response.ok) {
          const data = await response.json()
          setRealtimeData(data.data)
          setIsConnected(true)
        }
      } catch (error) {
        console.error('[Realtime] Failed to fetch realtime data:', error)
        setIsConnected(false)
      }
    }

    // Try WebSocket first
    if (useWebSocket) {
      connectWebSocket()
    } else {
      // Fallback to HTTP polling
      fetchRealtime()
      pollInterval = setInterval(fetchRealtime, 5000) as unknown as number
    }

    return () => {
      if (ws) {
        ws.close()
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
      }
      if (pollInterval) {
        clearInterval(pollInterval)
      }
    }
  }, [siteId, useWebSocket])

  const activeVisitors = realtimeData?.activeVisitors || 0

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Real-Time Activity</CardTitle>
        <Badge variant={isConnected ? 'default' : 'secondary'} className="text-xs">
          {isConnected ? (
            <>
              <Activity className="mr-1 h-3 w-3 animate-pulse" />
              Live
            </>
          ) : (
            'Offline'
          )}
        </Badge>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Visitor Count */}
          <div>
            <div className="text-3xl font-bold">{activeVisitors}</div>
            <p className="text-xs text-muted-foreground mt-1">
              active visitor{activeVisitors !== 1 ? 's' : ''} right now
            </p>
          </div>

          {/* Active Pages */}
          {realtimeData && realtimeData.pages.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Current Pages
              </h4>
              <div className="space-y-2">
                {realtimeData.pages.slice(0, 5).map((page, index) => (
                  <div
                    key={index}
                    className="flex items-start justify-between text-sm border-l-2 border-primary/20 pl-2 py-1"
                  >
                    <div className="flex-1 min-w-0 mr-2">
                      <div className="font-medium truncate" title={page.title}>
                        {page.title}
                      </div>
                      <div className="text-xs text-muted-foreground truncate" title={page.path}>
                        {page.path}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs font-medium whitespace-nowrap">
                      <Users className="h-3 w-3" />
                      {page.visitors}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {realtimeData && realtimeData.pages.length === 0 && activeVisitors === 0 && (
            <p className="text-xs text-muted-foreground">
              No active visitors at the moment
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
